/**
 * Sample specimen corpus for the landing-page demo.
 * Every entry is illustrative sample data — none of it is real user data.
 */

import ramenImg from "../assets/img/specimen-ramen.jpg";
import flightImg from "../assets/img/specimen-flight.jpg";
import sneakerImg from "../assets/img/specimen-sneaker.jpg";
import apartmentImg from "../assets/img/specimen-apartment.jpg";
import ticketImg from "../assets/img/specimen-ticket.jpg";
import recipeImg from "../assets/img/specimen-recipe.jpg";

export interface Specimen {
  id: string;
  catalogNo: string;
  title: string;
  kind: string;
  image: string;
  alt: string;
  savedAt: string;
  sourceApp: string;
  /**
   * Honest origin labeling: PhotosPicker imports don't reveal the source app,
   * so origin is 'user' (tagged by the user), 'inferred' (guessed, shown with
   * uncertainty), or 'unknown'. Image + timestamp are always reliable.
   */
  origin: "user" | "inferred" | "unknown";
  /** Simulated OCR + vision extraction text. */
  extracted: string;
  fields: { label: string; value: string }[];
  tags: string[];
  /** Demo confidence of the typed extraction (0–1). */
  confidence: number;
  action?: {
    kind: "reminder" | "calendar";
    title: string;
    detail: string;
    when: string;
  };
}

export const SPECIMENS: Specimen[] = [
  {
    id: "ramen",
    catalogNo: "CUR-0117",
    title: "Menya Hikari — ramen-ya in Gion",
    kind: "Restaurant card",
    image: ramenImg,
    alt: "Screenshot of a dark restaurant info page for Menya Hikari showing a ramen bowl photo, opening hours Open 18:00–02:00, a Cash only chip, and a map of Gion in Kyoto.",
    savedAt: "Sep 12, 2025",
    sourceApp: "Safari",
    origin: "user",
    extracted:
      "Menya Hikari (ひかり). Small ramen shop in Gion, Kyoto. Open 18:00–02:00, closed Mondays. Cash only. Near Hanamikoji-dori, north of Yasaka Shrine.",
    fields: [
      { label: "Hours", value: "18:00–02:00" },
      { label: "Closed", value: "Mondays" },
      { label: "Payment", value: "Cash only" },
      { label: "Area", value: "Gion, Kyoto" },
    ],
    tags: ["travel", "japan", "kyoto", "food", "restaurant", "ramen", "dinner", "gion"],
    confidence: 0.94,
    action: {
      kind: "reminder",
      title: "Dinner at Menya Hikari",
      detail: "Gion, Kyoto — open late, cash only, closed Mondays",
      when: "Fri, Nov 14 · 19:30",
    },
  },
  {
    id: "flight",
    catalogNo: "CUR-0104",
    title: "SFO → KIX flight confirmation",
    kind: "Booking confirmation",
    image: flightImg,
    alt: "Screenshot of a light flight confirmation page showing SFO to KIX, flight UA 837 on Thursday Nov 13, departure 10:45 AM, seat 21A Economy Plus, and confirmation code K7X-4QP.",
    savedAt: "Oct 2, 2025",
    sourceApp: "United app",
    origin: "user",
    extracted:
      "Flight confirmed. UA 837, SFO → KIX (Kansai). Thu, Nov 13, departs 10:45 AM. Seat 21A Economy+. Confirmation code K7X-4QP.",
    fields: [
      { label: "Route", value: "SFO → KIX" },
      { label: "Flight", value: "UA 837" },
      { label: "Departs", value: "Nov 13, 10:45 AM" },
      { label: "Conf. code", value: "K7X-4QP" },
    ],
    tags: ["travel", "flight", "japan", "booking", "united", "itinerary", "osaka"],
    confidence: 0.98,
    action: {
      kind: "calendar",
      title: "UA 837 SFO → KIX",
      detail: "Departs 10:45 AM · Seat 21A · Conf K7X-4QP",
      when: "Thu, Nov 13 · 10:45–14:20",
    },
  },
  {
    id: "sneaker",
    catalogNo: "CUR-0098",
    title: "Court Low '87 — Bone/Gum",
    kind: "Product page",
    image: sneakerImg,
    alt: "Screenshot of a shopping app product page for Court Low 87 sneakers in Bone/Gum, priced at $118 down from $145, with size chips and an Add to bag button.",
    savedAt: "Aug 29, 2025",
    sourceApp: "Shopping app",
    origin: "inferred",
    extracted:
      "Court Low '87 in Bone/Gum. $118, was $145. Only 3 left in size 9.5. Free returns within 30 days.",
    fields: [
      { label: "Price", value: "$118 (was $145)" },
      { label: "Your size", value: "9.5 — 3 left" },
      { label: "Returns", value: "30 days free" },
    ],
    tags: ["shopping", "sneakers", "shoes", "wishlist", "sale", "price"],
    confidence: 0.91,
    action: {
      kind: "reminder",
      title: "Decide on Court Low '87 (9.5)",
      detail: "$118 at last check — low stock. Still want them?",
      when: "Sun · 10:00",
    },
  },
  {
    id: "apartment",
    catalogNo: "CUR-0089",
    title: "Sunny studio — Mission District",
    kind: "Rental listing",
    image: apartmentImg,
    alt: "Screenshot of a rental listing for a sunny Mission District studio apartment at $2,350 per month, available December 1, with in-unit laundry and a Schedule a tour button.",
    savedAt: "Oct 20, 2025",
    sourceApp: "Messages",
    origin: "user",
    extracted:
      "Sunny studio, Mission District. $2,350/mo, available Dec 1. Studio, 1 bath, 480 sqft. In-unit laundry, no pets. Listed by Maya R., Lic. #01999212.",
    fields: [
      { label: "Rent", value: "$2,350/mo" },
      { label: "Available", value: "Dec 1" },
      { label: "Size", value: "480 sqft · studio" },
      { label: "Agent", value: "Maya R. #01999212" },
    ],
    tags: ["housing", "apartment", "rental", "mission", "moving", "listing"],
    confidence: 0.96,
    action: {
      kind: "reminder",
      title: "Message Maya R. re: Mission studio",
      detail: "Tour request — $2,350/mo, avail Dec 1. Lic. #01999212",
      when: "Tomorrow · 09:00",
    },
  },
  {
    id: "ticket",
    catalogNo: "CUR-0123",
    title: "Nadia Ozo at The Fillmore",
    kind: "Ticket",
    image: ticketImg,
    alt: "Screenshot of a dark concert ticket for Nadia Ozo with Palette Knife at The Fillmore on Saturday Oct 18 at 8 PM, GA floor, with a QR code and order number F-88104.",
    savedAt: "Sep 30, 2025",
    sourceApp: "Ticket app",
    origin: "inferred",
    extracted:
      "The Fillmore. Nadia Ozo w/ Palette Knife. Sat, Oct 18, 8:00 PM. GA Floor. Order #F-88104, 2 tickets. QR entry.",
    fields: [
      { label: "Venue", value: "The Fillmore" },
      { label: "Date", value: "Sat, Oct 18 · 8 PM" },
      { label: "Tickets", value: "2 · GA Floor" },
      { label: "Order", value: "#F-88104" },
    ],
    tags: ["concert", "ticket", "music", "fillmore", "event", "night-out"],
    confidence: 0.97,
    action: {
      kind: "calendar",
      title: "Nadia Ozo @ The Fillmore",
      detail: "Doors ~19:00 · Show 20:00 · 2 tickets, order #F-88104",
      when: "Sat, Oct 18 · 19:00–23:00",
    },
  },
  {
    id: "recipe",
    catalogNo: "CUR-0141",
    title: "Nasu Dengaku — Miso Eggplant",
    kind: "Recipe card",
    image: recipeImg,
    alt: "Screenshot of a cream recipe card for Nasu Dengaku miso eggplant showing 35 minutes, serves 2, and a checklist of ingredients including eggplants, miso, and mirin.",
    savedAt: "Oct 8, 2025",
    sourceApp: "Shared note",
    origin: "user",
    extracted:
      "Nasu Dengaku — Miso Eggplant. 35 min, serves 2. Ingredients: 2 Japanese eggplants, 3 tbsp white miso, 1 tbsp mirin, 1 tsp sugar, sesame and scallion to finish. Saved from @supperjournal.",
    fields: [
      { label: "Time", value: "35 min" },
      { label: "Serves", value: "2" },
      { label: "Key items", value: "Eggplant, miso, mirin" },
      { label: "Saved from", value: "@supperjournal" },
    ],
    tags: ["recipe", "cooking", "japanese", "eggplant", "dinner", "miso", "vegetarian"],
    confidence: 0.93,
    action: {
      kind: "reminder",
      title: "Pick up miso + eggplant",
      detail: "For nasu dengaku — also need mirin and scallions",
      when: "Sat · 11:00",
    },
  },
];

/** Suggested queries shown as chips in the demo. */
export const SAMPLE_QUERIES: string[] = [
  "that ramen place in Kyoto",
  "when does my flight leave",
  "the apartment Maya sent",
  "eggplant recipe",
  "sneakers that went on sale",
  "concert tickets",
];

export function scoreSpecimen(query: string, s: Specimen): number {
  const q = query.toLowerCase();
  const tokens = q.split(/[^a-z0-9']+/i).filter((t) => t.length > 1);
  if (tokens.length === 0) return 0;
  const hay = [s.title, s.kind, s.extracted, s.sourceApp, ...s.tags, ...s.fields.map((f) => f.value)]
    .join(" ")
    .toLowerCase();
  let score = 0;
  for (const t of tokens) {
    if (s.tags.some((tag) => tag === t || tag.startsWith(t))) score += 3;
    if (hay.includes(t)) score += 2;
    // small fuzzy: prefix match on words
    if (hay.split(/\W+/).some((w) => w.length > 3 && t.length > 3 && (w.startsWith(t) || t.startsWith(w)))) score += 1;
  }
  return score;
}
